/*
Backup ZIP exporter.

Builds a ZIP with meta.json + captures.json + photos/<uuid>.jpg using
fflate's synchronous zip builder. Synchronous is fine for personal scale
(a few hundred captures, photos ~200-500 KB each = a few hundred MB of
RAM peak); switching to fflate's streaming `Zip` would only matter if we
expected thousands of photos or wanted to free GC pressure.

The resulting Blob is handed to a hidden <a> element via createObjectURL
to trigger the browser's "save as" dialog. iOS Safari supports this since
13.4 (Files app destination); on desktop the file lands in the default
downloads folder.
*/

import { zipSync, strToU8 } from 'fflate'
import { listCaptures } from '../data/captureRepository'
import { loadPhoto } from '../data/photoStore'
import {
	kCurrentSchemaVersion,
	kMetaFilename,
	kCapturesFilename,
	kPhotosDir,
	type BackupMeta,
	type ExportedCapture,
} from './models'

export interface ExportResult
{
	captureCount: number
	photoCount: number
	photosMissing: number
	bytes: number
}

/*
Build a backup ZIP from the current IDB state and trigger a browser
download with the given suggested filename. Returns counts so the UI can
surface them in a toast ("12 captures, 11 photos").
*/
export async function exportBackup(): Promise<ExportResult>
{
	const vCaptures = await listCaptures()  // chronological order, pre-sorted by repo
	const vExported: ExportedCapture[] = vCaptures.map((inCap) => {
		const { id: _vId, ...vRest } = inCap  // strip the IDB autoincrement id
		return vRest
	})

	const vMeta: BackupMeta = {
		schemaVersion: kCurrentSchemaVersion,
		exportedAt: Date.now(),
		captureCount: vExported.length,
		appVersionName: '0.1.0',
		appVersionCode: 1,
		appPlatform: 'pwa',
	}

	const vEntries: Record<string, Uint8Array> = {}  // path -> bytes for fflate.zipSync
	vEntries[kMetaFilename] = strToU8(JSON.stringify(vMeta, null, 2))
	vEntries[kCapturesFilename] = strToU8(JSON.stringify(vExported))

	let vPhotoCount = 0  // photos successfully streamed into the zip
	let vMissing = 0  // photoFilename present but no Blob in IDB
	for (const vCap of vCaptures)
	{
		if (!vCap.photoFilename) continue
		const vBlob = await loadPhoto(vCap.photoFilename)
		if (!vBlob) { vMissing += 1; continue }
		const vBuf = new Uint8Array(await vBlob.arrayBuffer())  // photo bytes
		vEntries[`${kPhotosDir}${vCap.photoFilename}`] = vBuf
		vPhotoCount += 1
	}

	const vZipBytes = zipSync(vEntries, { level: 0 })  // store-only; JPGs are already compressed
	const vBlob = new Blob([vZipBytes as BlobPart], { type: 'application/zip' })
	const vFilename = filenameForToday(vMeta.exportedAt)
	triggerDownload(vBlob, vFilename)

	return {
		captureCount: vExported.length,
		photoCount: vPhotoCount,
		photosMissing: vMissing,
		bytes: vZipBytes.length,
	}
}

/*
Build a stable filename of the form "canopaname-backup-YYYY-MM-DD.zip".
Matches the Android exporter's BackupFilename.kt convention so users can
expect the same name on both platforms.
*/
function filenameForToday(inExportedAt: number): string
{
	const vDate = new Date(inExportedAt)  // local time — backup belongs to the user's day
	const vY = vDate.getFullYear()
	const vM = String(vDate.getMonth() + 1).padStart(2, '0')
	const vD = String(vDate.getDate()).padStart(2, '0')
	return `canopaname-backup-${vY}-${vM}-${vD}.zip`
}

/*
Trigger a browser download for the given blob via a hidden anchor.
Object URL is revoked on the next animation frame, after the click has
been dispatched and the browser has captured the URL.
*/
function triggerDownload(inBlob: Blob, inFilename: string): void
{
	const vUrl = URL.createObjectURL(inBlob)  // ephemeral object URL
	const vAnchor = document.createElement('a')
	vAnchor.href = vUrl
	vAnchor.download = inFilename
	vAnchor.style.display = 'none'
	document.body.appendChild(vAnchor)
	vAnchor.click()
	requestAnimationFrame(() => {
		document.body.removeChild(vAnchor)
		URL.revokeObjectURL(vUrl)
	})
}
